import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { useGetMasterWithSubsQuery } from 'features/master/api/master.api';
import { SectionBlock, Snackbar } from 'shared/ui';
import type { SnackbarVariant } from 'shared/ui';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import { useGetIntegrationQuery, useGetIntegrationsQuery } from '../api/integrations';
import { ID_TEXT_CLASS } from '../lib/id';
import { formatJson } from '../lib/json';
import { getWebhookResources } from '../model/integration';
import type { Integration, IntegrationDetail } from '../model/integration';
import ConfigSection from './ConfigSection';
import CredentialsSection from './CredentialsSection';
import IntegrationFormDialog from './IntegrationFormDialog';
import MappingsSection from './MappingsSection';
import type { MappableProject } from './MappingsSection';
import ProcessesSection from './ProcessesSection';
import WebhookSection from './WebhookSection';

// Setup walkthrough deck published as a claude.ai artifact; it only opens for the people it is shared with
const TUTORIAL_URL = 'https://claude.ai/artifact/KuGjq29JNmawU62C17Q1wv';

const INTEGRATION_PARAM = 'integration';
const TAB_PARAM = 'tab';

// The tab is in the URL (?tab=procesos), so a link opens the view it points to
const TABS = [
  { value: 'general', label: 'General' },
  { value: 'procesos', label: 'Procesos' },
  { value: 'config', label: 'Config' },
  { value: 'mapeo', label: 'Mapeo' },
] as const;
type TabValue = (typeof TABS)[number]['value'];

const isTabValue = (value: string | null): value is TabValue => TABS.some((tab) => tab.value === value);
const RUN_POLLING_MS = 3000;
const RUN_WATCH_MS = 60_000;

interface SnackbarState {
  /** Keys the Snackbar, so a notice that replaces an open one gets its own close timer */
  id: number;
  message: string;
  variant: SnackbarVariant;
}

const DetailRow = ({ label, children }: { label: string; children: ReactNode }): JSX.Element => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-text-muted">{label}</span>
    <span className="text-sm text-text-default break-all">{children}</span>
  </div>
);

interface IntegrationDataProps {
  integration: IntegrationDetail;
  onEdit: () => void;
}

const IntegrationData = ({ integration, onEdit }: IntegrationDataProps): JSX.Element => (
  <SectionBlock
    title={`Detalle de integración: ${integration.label} · ${integration.provider}`}
    action={
      <Button size="sm" variant="outline" onClick={onEdit}>
        Editar
      </Button>
    }
  >
    {/* The ID is a short number: a narrow first column leaves the room to the credential and the slug */}
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[4rem_repeat(3,minmax(0,1fr))]">
      <DetailRow label="ID">
        <span className={ID_TEXT_CLASS}>{integration.id}</span>
      </DetailRow>
      <DetailRow label="Credencial">
        {integration.credential ? (
          <>
            {integration.credential.label} <span className={ID_TEXT_CLASS}>#{integration.credential.id}</span>
          </>
        ) : (
          'Sin credencial'
        )}
      </DetailRow>
      <DetailRow label="ID de cuenta externa">{integration.externalAccountId ?? '—'}</DetailRow>
      <DetailRow label="Webhook slug">
        {integration.webhookSlug ? <span className="font-mono">{integration.webhookSlug}</span> : '—'}
      </DetailRow>
    </div>
    {integration.credential ? (
      <details className="mt-4">
        <summary className="cursor-pointer text-xs text-text-muted">Ver configuración de la credencial</summary>
        <pre className="mt-1 overflow-auto rounded bg-bg-surface-raised p-2 text-[11px]">
          {formatJson(integration.credential.settings)}
        </pre>
      </details>
    ) : null}
  </SectionBlock>
);

interface IntegrationPickerProps {
  integrations: Integration[];
  selectedId: number | undefined;
  onSelect: (id: number) => void;
}

// The tabs other than General show one integration's data, so they say which one and let you switch
const IntegrationPicker = ({ integrations, selectedId, onSelect }: IntegrationPickerProps): JSX.Element | null => {
  const selected = integrations.find(({ id }) => id === selectedId);
  if (!selected) return null;
  if (integrations.length === 1) {
    return (
      <span className="text-sm text-text-muted">
        {selected.label} · <span className="font-mono text-xs">{selected.provider}</span>{' '}
        <span className={ID_TEXT_CLASS}>#{selected.id}</span>
      </span>
    );
  }
  return (
    <Select value={selected.id.toString()} onValueChange={(value) => onSelect(Number(value))}>
      <SelectTrigger size="sm" className="w-64">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {integrations.map(({ id, label, provider }) => (
          <SelectItem key={id} value={id.toString()}>
            {label} · {provider} <span className={ID_TEXT_CLASS}>#{id}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

/*
Admin console for the integrations model (Integration, IntegrationCredential, IntegrationProcess, IntegrationMapping).
Reached only by typing /project/:masterProjectId/crm-integrations: nothing in the app links here on purpose, and the
/admin/integrations routes behind it answer only to admins.
*/
const CrmIntegrationsPage = (): JSX.Element => {
  const masterId = Number(useParams().masterProjectId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState<Integration | 'new' | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);

  const { data: master } = useGetMasterWithSubsQuery(masterId, { skip: !masterId });
  const {
    data: integrations = [],
    isLoading,
    error,
    refetch: refetchList,
  } = useGetIntegrationsQuery(masterId, { skip: !masterId });

  const paramId = Number(searchParams.get(INTEGRATION_PARAM));
  const selectedId = integrations.some(({ id }) => id === paramId) ? paramId : integrations[0]?.id;
  const tabParam = searchParams.get(TAB_PARAM);
  const tab: TabValue = isTabValue(tabParam) ? tabParam : 'general';

  /*
  A run is a Cloud Task, so its result lands some seconds after the request. The detail is polled for a while after
  a run is requested, and for as long as a process is running, so dry run results show up without reloading.
  */
  const [runRequestedAt, setRunRequestedAt] = useState<number | null>(null);
  const [isAnyRunning, setIsAnyRunning] = useState(false);
  const { data: detail, refetch: refetchDetail } = useGetIntegrationQuery(selectedId ?? 0, {
    skip: !selectedId,
    pollingInterval: runRequestedAt !== null || isAnyRunning ? RUN_POLLING_MS : 0,
  });

  useEffect(() => {
    setIsAnyRunning(detail?.processes.some(({ runningSince }) => runningSince !== null) ?? false);
  }, [detail]);

  useEffect(() => {
    if (runRequestedAt === null) return undefined;
    const timeout = setTimeout(() => setRunRequestedAt(null), RUN_WATCH_MS);
    return (): void => clearTimeout(timeout);
  }, [runRequestedAt]);

  const handleRunRequested = useCallback(() => setRunRequestedAt(Date.now()), []);

  // A mapping points to a subproject, or to the master itself when it has none
  const mappableProjects = useMemo<MappableProject[]>(() => {
    if (!master) return [];
    const subProjects = master.subProjects ?? [];
    return subProjects.length > 0
      ? subProjects.map(({ id, name }) => ({ id, name }))
      : [{ id: master.id, name: master.name }];
  }, [master]);

  const notify = useCallback((message: string, variant: SnackbarVariant = 'success') => {
    setSnackbar((previous) => ({ id: (previous?.id ?? 0) + 1, message, variant }));
  }, []);
  const closeSnackbar = useCallback(() => setSnackbar(null), []);

  // Each param changes on its own, so switching integration keeps the tab and the other way around
  const setParam = (key: string, value: string): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(key, value);
        return next;
      },
      { replace: true }
    );
  };
  const selectIntegration = (id: number): void => setParam(INTEGRATION_PARAM, id.toString());
  const selectTab = (value: string): void => setParam(TAB_PARAM, value);

  const hasDetail = !!detail && detail.id === selectedId;
  const detailPlaceholder =
    integrations.length === 0 ? (
      <p className="m-0 text-sm text-text-muted">Crea una integración en la pestaña General.</p>
    ) : (
      <Skeleton className="h-24 rounded-xl" />
    );

  const refresh = (): void => {
    refetchList();
    if (selectedId) refetchDetail();
  };

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="mb-1 font-body text-xs font-bold uppercase tracking-widest text-text-subtle">
            {master?.name ?? `Proyecto #${masterId}`}
          </p>
          <h2 className="m-0 font-display text-2xl font-bold text-text-default">Integraciones CRM</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh}>
            Actualizar
          </Button>
          <Button size="sm" onClick={() => setEditing('new')}>
            Nueva integración
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={TUTORIAL_URL} target="_blank" rel="noreferrer">
              Tutorial
            </a>
          </Button>
        </div>
      </header>

      {isLoading ? <Skeleton className="h-24 rounded-xl" /> : null}

      {error ? (
        <div className="rounded-xl border border-border-subtle bg-bg-surface px-5 py-4 text-sm text-error">
          {extractErrorMessage(error, 'Error al cargar las integraciones')}
          <p className="m-0 mt-1 text-xs text-text-muted">Esta página usa rutas que requieren un usuario admin.</p>
        </div>
      ) : null}

      {!isLoading && !error ? (
        <Tabs value={tab} onValueChange={selectTab}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList>
              {TABS.map(({ value, label }) => (
                <TabsTrigger key={value} value={value}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tab !== 'general' ? (
              <IntegrationPicker integrations={integrations} selectedId={selectedId} onSelect={selectIntegration} />
            ) : null}
          </div>

          {/* Mounted while hidden, so an unsaved config edit survives a look at another tab */}
          <TabsContent value="general" forceMount className="mt-4 flex flex-col gap-4 data-[state=inactive]:hidden">
            <SectionBlock title={`Integraciones configuradas (${integrations.length})`}>
              {integrations.length === 0 ? (
                <p className="m-0 text-sm text-text-muted">Esta inmobiliaria aún no tiene integraciones.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">ID</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Credencial</TableHead>
                      <TableHead>Cuenta externa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {integrations.map((integration) => (
                      <TableRow
                        key={integration.id}
                        onClick={() => selectIntegration(integration.id)}
                        className={cn('cursor-pointer', integration.id === selectedId && 'bg-bg-surface-raised')}
                      >
                        <TableCell className={ID_TEXT_CLASS}>{integration.id}</TableCell>
                        <TableCell className="font-medium">{integration.label}</TableCell>
                        <TableCell className="font-mono text-xs">{integration.provider}</TableCell>
                        <TableCell className={ID_TEXT_CLASS}>{integration.credentialId ?? '—'}</TableCell>
                        <TableCell className="text-xs">{integration.externalAccountId ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SectionBlock>
            {hasDetail ? (
              <>
                <IntegrationData integration={detail} onEdit={() => setEditing(detail)} />
                {getWebhookResources(detail.provider).length > 0 ? (
                  <WebhookSection integration={detail} notify={notify} />
                ) : null}
              </>
            ) : null}
            <CredentialsSection
              key={detail?.provider}
              initialProvider={detail?.provider}
              currentCredentialId={detail?.credentialId ?? null}
            />
          </TabsContent>

          <TabsContent value="procesos" forceMount className="mt-4 data-[state=inactive]:hidden">
            {hasDetail ? (
              <ProcessesSection integration={detail} notify={notify} onRunRequested={handleRunRequested} />
            ) : (
              detailPlaceholder
            )}
          </TabsContent>

          <TabsContent value="config" forceMount className="mt-4 data-[state=inactive]:hidden">
            {hasDetail ? (
              <ConfigSection
                key={detail.id}
                integration={detail}
                subProjects={master?.subProjects?.map(({ id, name }) => ({ id, name })) ?? []}
                notify={notify}
              />
            ) : (
              detailPlaceholder
            )}
          </TabsContent>

          <TabsContent value="mapeo" forceMount className="mt-4 data-[state=inactive]:hidden">
            {hasDetail ? (
              <MappingsSection integration={detail} projects={mappableProjects} notify={notify} />
            ) : (
              detailPlaceholder
            )}
          </TabsContent>
        </Tabs>
      ) : null}

      {editing ? (
        <IntegrationFormDialog
          projectId={masterId}
          integration={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            selectIntegration(saved.id);
            notify(editing === 'new' ? 'Integración creada' : 'Integración actualizada');
          }}
        />
      ) : null}

      <Snackbar
        key={snackbar?.id}
        open={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ''}
        onClose={closeSnackbar}
        duration={4000}
      />
    </section>
  );
};

export default CrmIntegrationsPage;
